import type { Theme } from "@structurify/shared-types";

type FieldId =
  | "claim_number"
  | "date_of_loss"
  | "policy_number"
  | "insured_name"
  | "insured_dob"
  | "insured_phone"
  | "insured_address"
  | "loss_location"
  | "loss_type"
  | "loss_description"
  | "estimated_amount"
  | "deductible"
  | "adjuster_name"
  | "signature_date";

interface FieldBbox {
  x: number;
  y: number;
  w: number;
  h: number;
}

const FIELD_BBOXES: Record<FieldId, FieldBbox> = {
  claim_number:     { x: 432, y: 78,  w: 140, h: 22 },
  date_of_loss:     { x: 432, y: 106, w: 140, h: 22 },
  policy_number:    { x: 432, y: 134, w: 140, h: 22 },
  insured_name:     { x: 142, y: 198, w: 220, h: 18 },
  insured_dob:      { x: 142, y: 232, w: 110, h: 18 },
  insured_phone:    { x: 282, y: 232, w: 130, h: 18 },
  insured_address:  { x: 142, y: 266, w: 410, h: 18 },
  loss_location:    { x: 142, y: 350, w: 410, h: 18 },
  loss_type:        { x: 142, y: 384, w: 200, h: 18 },
  loss_description: { x: 40,  y: 422, w: 532, h: 64 },
  estimated_amount: { x: 142, y: 524, w: 130, h: 18 },
  deductible:       { x: 282, y: 524, w: 110, h: 18 },
  adjuster_name:    { x: 142, y: 588, w: 200, h: 18 },
  signature_date:   { x: 432, y: 712, w: 140, h: 22 },
};

interface HighlightRectProps {
  id: FieldId;
  activeField: string | null;
  hoveredField: string | null;
  accent: string;
}

function HighlightRect({ id, activeField, hoveredField, accent }: HighlightRectProps) {
  const f = FIELD_BBOXES[id];
  const isActive = activeField === id;
  const isHover = hoveredField === id;
  return (
    <rect
      x={f.x - 4}
      y={f.y - 4}
      width={f.w + 8}
      height={f.h + 8}
      rx="2"
      fill={isActive ? accent : isHover ? `${accent}33` : "transparent"}
      fillOpacity={isActive ? 0.28 : 1}
      stroke={isActive ? accent : isHover ? `${accent}88` : "transparent"}
      strokeWidth={isActive ? 1.25 : 1}
      style={{ transition: "all 280ms cubic-bezier(.2,.7,.2,1)", mixBlendMode: "multiply" }}
    />
  );
}

interface Props {
  activeField: string | null;
  hoveredField: string | null;
  theme: Theme;
  scale?: number;
  showRedactions?: boolean;
}

export function ClaimDocument({ activeField, hoveredField, theme, scale = 1, showRedactions = true }: Props) {
  const { ink, docSubtle: subtle, docPaper: paper, accent } = theme;

  return (
    <div
      style={{
        transform: `scale(${scale})`,
        transformOrigin: "top center",
        filter: "drop-shadow(0 1px 0 rgba(0,0,0,0.04)) drop-shadow(0 12px 32px rgba(20,16,10,0.08))",
      }}
    >
      <svg
        viewBox="0 0 612 792"
        width="612"
        height="792"
        style={{ background: paper, display: "block", borderRadius: 2 }}
      >
        <defs>
          <pattern id="grain" patternUnits="userSpaceOnUse" width="3" height="3">
            <rect width="3" height="3" fill={paper} />
            <circle cx="0.5" cy="0.5" r="0.25" fill={ink} fillOpacity="0.015" />
          </pattern>
          <linearGradient id="edge" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor={ink} stopOpacity="0.03" />
            <stop offset="0.05" stopColor={ink} stopOpacity="0" />
          </linearGradient>
        </defs>
        <rect width="612" height="792" fill="url(#grain)" />
        <rect width="612" height="6" fill="url(#edge)" />

        <text x="40" y="48" fontFamily="ui-serif, Georgia, serif" fontSize="16" fontWeight="600" fill={ink} letterSpacing="0.2">
          MERIDIAN MUTUAL INSURANCE
        </text>
        <text x="40" y="64" fontFamily="ui-monospace, monospace" fontSize="8" fill={subtle} letterSpacing="1">
          PROPERTY &amp; CASUALTY · CLAIM FORM ACR-104
        </text>
        <line x1="40" y1="72" x2="572" y2="72" stroke={ink} strokeOpacity="0.5" strokeWidth="0.75" />

        <text x="380" y="92" fontFamily="ui-monospace, monospace" fontSize="8" fill={subtle} letterSpacing="0.5">CLAIM NO.</text>
        <text x="438" y="92" fontFamily="ui-monospace, monospace" fontSize="11" fill={ink}>CL-2026-0418-7732</text>
        <line x1="430" y1="98" x2="572" y2="98" stroke={ink} strokeOpacity="0.3" />

        <text x="380" y="120" fontFamily="ui-monospace, monospace" fontSize="8" fill={subtle} letterSpacing="0.5">DATE OF LOSS</text>
        <text x="438" y="120" fontFamily="ui-monospace, monospace" fontSize="11" fill={ink}>03 / 14 / 2026</text>
        <line x1="430" y1="126" x2="572" y2="126" stroke={ink} strokeOpacity="0.3" />

        <text x="380" y="148" fontFamily="ui-monospace, monospace" fontSize="8" fill={subtle} letterSpacing="0.5">POLICY NO.</text>
        <text x="438" y="148" fontFamily="ui-monospace, monospace" fontSize="11" fill={ink}>HO-554918-A</text>
        <line x1="430" y1="154" x2="572" y2="154" stroke={ink} strokeOpacity="0.3" />

        <text x="40" y="184" fontFamily="ui-serif, Georgia, serif" fontSize="11" fontWeight="600" fill={ink} letterSpacing="0.5">§1  INSURED PARTY</text>
        <line x1="40" y1="190" x2="572" y2="190" stroke={ink} strokeOpacity="0.15" />

        <text x="40" y="210" fontFamily="ui-monospace, monospace" fontSize="8" fill={subtle}>NAME</text>
        <text x="142" y="212" fontFamily="ui-serif, Georgia, serif" fontSize="13" fill={ink}>Eleanor R. Whitfield</text>

        <text x="40" y="244" fontFamily="ui-monospace, monospace" fontSize="8" fill={subtle}>DOB</text>
        <text x="142" y="246" fontFamily="ui-serif, Georgia, serif" fontSize="12" fill={ink}>August 22, 1971</text>

        <text x="240" y="244" fontFamily="ui-monospace, monospace" fontSize="8" fill={subtle}>PHONE</text>
        <text x="282" y="246" fontFamily="ui-serif, Georgia, serif" fontSize="12" fill={ink}>
          {showRedactions ? "(415) 555-0148" : "(415) ███-████"}
        </text>

        <text x="40" y="278" fontFamily="ui-monospace, monospace" fontSize="8" fill={subtle}>ADDRESS</text>
        <text x="142" y="280" fontFamily="ui-serif, Georgia, serif" fontSize="12" fill={ink}>
          2418 Larkin Street, Apt 6, San Francisco, CA 94109
        </text>

        <text x="40" y="332" fontFamily="ui-serif, Georgia, serif" fontSize="11" fontWeight="600" fill={ink} letterSpacing="0.5">§2  PARTICULARS OF LOSS</text>
        <line x1="40" y1="338" x2="572" y2="338" stroke={ink} strokeOpacity="0.15" />

        <text x="40" y="362" fontFamily="ui-monospace, monospace" fontSize="8" fill={subtle}>LOCATION</text>
        <text x="142" y="364" fontFamily="ui-serif, Georgia, serif" fontSize="12" fill={ink}>
          2418 Larkin Street, kitchen and adjoining hallway
        </text>

        <text x="40" y="396" fontFamily="ui-monospace, monospace" fontSize="8" fill={subtle}>TYPE</text>
        <text x="142" y="398" fontFamily="ui-serif, Georgia, serif" fontSize="12" fill={ink}>Water damage — burst supply line</text>

        <text x="40" y="430" fontFamily="ui-monospace, monospace" fontSize="8" fill={subtle}>NARRATIVE</text>
        <foreignObject x="40" y="436" width="532" height="58">
          <div
            // @ts-expect-error xmlns is required on foreignObject children
            xmlns="http://www.w3.org/1999/xhtml"
            style={{ fontFamily: "ui-serif, Georgia, serif", fontSize: 11.5, lineHeight: 1.45, color: ink, paddingTop: 4 }}
          >
            On the evening of March 14, the under-sink supply line in the kitchen failed at the compression fitting,
            releasing water continuously for approximately three hours before the claimant returned home. Damage extends
            to the kitchen flooring, lower cabinetry, and the hallway runner.
          </div>
        </foreignObject>

        <text x="40" y="510" fontFamily="ui-serif, Georgia, serif" fontSize="11" fontWeight="600" fill={ink} letterSpacing="0.5">§3  ESTIMATED LOSS</text>
        <line x1="40" y1="516" x2="572" y2="516" stroke={ink} strokeOpacity="0.15" />

        <text x="40" y="536" fontFamily="ui-monospace, monospace" fontSize="8" fill={subtle}>AMOUNT (USD)</text>
        <text x="142" y="538" fontFamily="ui-serif, Georgia, serif" fontSize="13" fontWeight="500" fill={ink}>$ 18,420.00</text>

        <text x="240" y="536" fontFamily="ui-monospace, monospace" fontSize="8" fill={subtle}>DEDUCTIBLE</text>
        <text x="282" y="538" fontFamily="ui-serif, Georgia, serif" fontSize="13" fill={ink}>$ 1,500.00</text>

        <text x="40" y="574" fontFamily="ui-serif, Georgia, serif" fontSize="11" fontWeight="600" fill={ink} letterSpacing="0.5">§4  ASSIGNED ADJUSTER</text>
        <line x1="40" y1="580" x2="572" y2="580" stroke={ink} strokeOpacity="0.15" />

        <text x="40" y="600" fontFamily="ui-monospace, monospace" fontSize="8" fill={subtle}>NAME</text>
        <text x="142" y="602" fontFamily="ui-serif, Georgia, serif" fontSize="12" fill={ink}>M. Castellano</text>

        <line x1="40" y1="700" x2="572" y2="700" stroke={ink} strokeOpacity="0.2" />
        <text x="40" y="722" fontFamily="ui-monospace, monospace" fontSize="8" fill={subtle}>SIGNATURE — INSURED</text>
        <text x="40" y="744" fontFamily="ui-serif, Georgia, serif" fontSize="14" fontStyle="italic" fill={ink}>Eleanor Whitfield</text>
        <line x1="40" y1="750" x2="240" y2="750" stroke={ink} strokeOpacity="0.4" />

        <text x="380" y="722" fontFamily="ui-monospace, monospace" fontSize="8" fill={subtle}>DATE SIGNED</text>
        <text x="438" y="724" fontFamily="ui-monospace, monospace" fontSize="11" fill={ink}>03 / 18 / 2026</text>
        <line x1="430" y1="730" x2="572" y2="730" stroke={ink} strokeOpacity="0.3" />

        <text x="40" y="772" fontFamily="ui-monospace, monospace" fontSize="7" fill={subtle} letterSpacing="1">
          PAGE 1 OF 1 · FORM ACR-104 · REV. 2024.06
        </text>

        <g style={{ pointerEvents: "none" }}>
          {(Object.keys(FIELD_BBOXES) as FieldId[]).map((id) => (
            <HighlightRect key={id} id={id} activeField={activeField} hoveredField={hoveredField} accent={accent} />
          ))}
        </g>
      </svg>
    </div>
  );
}
